import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button } from "@repo/o37-ui";
import { Plus, Pencil, Settings } from "lucide-react";
import { FundraisingFund, FundraisingContact, FundraisingTermSheet, calculateTermSheetMetrics } from '../../types';
import TermSheetForm from '../../components/Fundraising/TermSheetForm';
import ContactForm from '../../components/Fundraising/ContactForm';

interface FundOverviewProps {
  fund: FundraisingFund;
  onUpdate: () => void;
}

export default function FundOverview({ fund, onUpdate }: FundOverviewProps) {
  const navigate = useNavigate();
  const [showTermSheetForm, setShowTermSheetForm] = useState(false);
  const [editingTermSheet, setEditingTermSheet] = useState<FundraisingTermSheet | null>(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<FundraisingContact | null>(null);

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Fund Details */}
          <Card>
            <CardHeader>
              <CardTitle>Details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {fund.thesis && (
                <div>
                  <label className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Investment Thesis</label>
                  <p className="text-sm text-neutral-800 mt-1">{fund.thesis}</p>
                </div>
              )}
              
              {(fund.typicalTicketMin || fund.typicalTicketMax) && (
                <div>
                  <label className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Typical Ticket</label>
                  <p className="text-sm text-neutral-800 mt-1">
                    {fund.typicalTicketMin && fund.typicalTicketMax ? (
                      `${formatCurrency(fund.typicalTicketMin)} - ${formatCurrency(fund.typicalTicketMax)}`
                    ) : fund.typicalTicketMin ? (
                      `${formatCurrency(fund.typicalTicketMin)}+`
                    ) : (
                      `Up to ${formatCurrency(fund.typicalTicketMax!)}`
                    )}
                  </p>
                </div>
              )}

              {fund.portfolioCompanies && fund.portfolioCompanies.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Portfolio Companies</label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {fund.portfolioCompanies.map((company, i) => (
                      <span key={i} className="text-xs bg-neutral-100 px-2 py-0.5 rounded">
                        {company}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {fund.notes && (
                <div>
                  <label className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Notes</label>
                  <p className="text-sm text-neutral-600 mt-1 whitespace-pre-wrap">{fund.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contacts */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Contacts</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingContact(null);
                  setShowContactForm(true);
                }}
                icon={<Plus className="w-4 h-4" />}
                text="Add Contact"
              />
            </CardHeader>
            <CardContent className="p-6">
              {!fund.contacts || fund.contacts.length === 0 ? (
                <p className="text-sm text-neutral-500 text-center py-4">No contacts yet</p>
              ) : (
                <div className="space-y-3">
                  {fund.contacts.map(contact => (
                    <div key={contact.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{contact.name}</span>
                          {contact.isPrimary && (
                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Primary</span>
                          )}
                        </div>
                        <div className="text-xs text-neutral-500">
                          {contact.title && <span>{contact.title}</span>}
                          {contact.title && contact.email && <span> · </span>}
                          {contact.email && <span>{contact.email}</span>}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingContact(contact);
                          setShowContactForm(true);
                        }}
                        icon={<Pencil className="w-3 h-3" />}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Term Sheets */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle>Term Sheets</CardTitle>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/fundraising/term-sheet-settings')}
                  className="h-6 w-6 p-0 text-neutral-400 hover:text-neutral-600"
                  icon={<Settings className="w-3.5 h-3.5" />}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setEditingTermSheet(null);
                  setShowTermSheetForm(true);
                }}
                icon={<Plus className="w-4 h-4" />}
                text="Add Term Sheet"
              />
            </CardHeader>
            <CardContent className="p-6">
              {!fund.termSheets || fund.termSheets.length === 0 ? (
                <p className="text-sm text-neutral-500 text-center py-4">No term sheets yet</p>
              ) : (
                <div className="space-y-4">
                  {fund.termSheets.map(ts => {
                    const metrics = ts.preMoneyValuation 
                      ? calculateTermSheetMetrics(ts.investmentAmount, ts.preMoneyValuation)
                      : null;
                    
                    return (
                      <div key={ts.id} className="p-4 border border-neutral-200 rounded-xl">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="text-lg font-semibold">{formatCurrency(ts.investmentAmount)}</div>
                            <div className="text-xs text-neutral-500">
                              at {formatCurrency(ts.preMoneyValuation || 0)} valuation
                            </div>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            ts.status === 'accepted' ? 'bg-emerald-100 text-emerald-700' :
                            ts.status === 'declined' ? 'bg-red-100 text-red-700' :
                            ts.status === 'expired' ? 'bg-neutral-100 text-neutral-500' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {ts.status.charAt(0).toUpperCase() + ts.status.slice(1)}
                          </span>
                        </div>
                        
                        {metrics && (
                          <div className="grid grid-cols-3 gap-4 p-3 bg-neutral-50 rounded-lg">
                            <div>
                              <div className="text-xs text-neutral-500">Post-Money</div>
                              <div className="text-sm font-medium">{formatCurrency(metrics.postMoneyValuation)}</div>
                            </div>
                            <div>
                              <div className="text-xs text-neutral-500">Dilution</div>
                              <div className="text-sm font-medium">{metrics.dilutionPercentage.toFixed(1)}%</div>
                            </div>
                            <div>
                              <div className="text-xs text-neutral-500">Liq. Pref</div>
                              <div className="text-sm font-medium">{ts.liquidationPreference}x</div>
                            </div>
                          </div>
                        )}

                        <div className="mt-3 flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingTermSheet(ts);
                              setShowTermSheetForm(true);
                            }}
                            icon={<Pencil className="w-3 h-3" />}
                            text="Edit"
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Right Column - Empty for now as Activity is moved to its own page, but could be used for other widgets */}
        <div className="space-y-6">
           {/* Add any overview specific side widgets here if needed */}
        </div>
      </div>

      {/* Term Sheet Modal */}
      {showTermSheetForm && (
        <TermSheetForm
          fundId={fund.id}
          termSheet={editingTermSheet}
          onClose={() => {
            setShowTermSheetForm(false);
            setEditingTermSheet(null);
          }}
          onSaved={() => {
            setShowTermSheetForm(false);
            setEditingTermSheet(null);
            onUpdate();
          }}
        />
      )}

      {/* Contact Modal */}
      {showContactForm && (
        <ContactForm
          fundId={fund.id}
          contact={editingContact}
          onClose={() => {
            setShowContactForm(false);
            setEditingContact(null);
          }}
          onSaved={() => {
            setShowContactForm(false);
            setEditingContact(null);
            onUpdate();
          }}
        />
      )}
    </div>
  );
}
